import React, { useEffect, useState } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

// Context Providers
import { AuthProvider, useAuth } from './context/AuthContext';
import { UIProvider, useUI } from './context/UIContext';
import { ProjectProvider } from './context/ProjectContext';

// Hooks
import { usePushNotifications } from './hooks/usePushNotifications';

// Services
import { supabase } from './services';

// Components
import { 
  ErrorBoundary, 
  FullPageLoader, 
  Toast, 
  OfflineBanner,
  UpdatePrompt,
  SkipLink,
} from './components/common';
import Sidebar from './components/layout/Sidebar';
import Navbar from './components/layout/Navbar';

// Pages
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import AdminUsers from './pages/AdminUsers';
import Profile from './pages/Profile';

// Types
import { Role } from './types';

// ============================================
// Auth Callback Handler
// ============================================

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the session after email confirmation
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Auth callback error:', error);
          navigate('/login');
          return;
        }

        if (session?.user) {
          // Check if user is invited (needs to set password)
          const isInvited = session.user.user_metadata?.is_invited;
          
          if (isInvited) {
            navigate('/set-password');
          } else {
            navigate('/');
          }
        } else {
          navigate('/login');
        }
      } catch (err) {
        console.error('Callback processing error:', err);
        navigate('/login');
      } finally {
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [navigate]);

  if (isProcessing) {
    return <FullPageLoader message="Confirming your account..." />;
  }

  return null;
};

// ============================================
// Private Route Component
// ============================================

interface PrivateRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, adminOnly }) => {
  const { currentUser, isLoading, isInvited, authError } = useAuth();
  const { isMobileSidebarOpen, setMobileSidebarOpen } = useUI();
  const location = useLocation();

  // Initialize push notifications for authenticated users
  usePushNotifications();

  // Show loading state
  if (isLoading) {
    return <FullPageLoader message="Establishing Cloud Session..." />;
  }

  // Redirect to login if not authenticated or error
  if (authError || !currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Force invited users to set password page
  if (isInvited && location.pathname !== '/set-password') {
    return <Navigate to="/set-password" replace />;
  }

  // Check admin access
  if (adminOnly && currentUser.role !== Role.ADMIN) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden relative">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <main 
          id="main-content"
          className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col relative scroll-smooth"
          role="main"
          aria-label="Main content"
        >
          <ErrorBoundary>
            {children}
          </ErrorBoundary>
        </main>
      </div>
      
      {/* Mobile sidebar overlay */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 md:hidden backdrop-blur-sm transition-opacity"
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

// ============================================
// App Routes Component
// ============================================

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <PrivateRoute>
            <Projects />
          </PrivateRoute>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <PrivateRoute>
            <ProjectDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <PrivateRoute>
            <Profile />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <PrivateRoute adminOnly>
            <AdminUsers />
          </PrivateRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// ============================================
// Main App Component
// ============================================

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <UIProvider>
          <ProjectProvider>
            <HashRouter>
              {/* Skip link for keyboard navigation */}
              <SkipLink />
              
              {/* Main app routes */}
              <AppRoutes />
              
              {/* Global UI components */}
              <Toast />
              <OfflineBanner />
              <UpdatePrompt />
            </HashRouter>
          </ProjectProvider>
        </UIProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
